import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import {
    Box,
    TextField,
    Button,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    Checkbox,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
    Alert,
    Tooltip,
    Divider,
    Grid,
    Card,
    CardContent,
    Chip, // Added for displaying selected cohorts
    OutlinedInput // Added for multi-select Chip display
} from "@mui/material";
import { AddCircleOutline, FileCopyOutlined as DuplicateIcon, Edit, Delete } from '@mui/icons-material';
import config from './config.js';
import { isTokenExpired } from './auth'; 

function SurveyManagement() {
    // --- State for Create Form ---
    const [surveyName, setSurveyName] = useState("");
    const [description, setDescription] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [selectedCohortIds, setSelectedCohortIds] = useState([]); // Changed from single cohort to array
    const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
    const [formError, setFormError] = useState(null);
    const [formSuccess, setFormSuccess] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false); 

    // --- State for Data Lists ---
    const [cohorts, setCohorts] = useState([]);       
    const [questions, setQuestions] = useState([]);     
    const [surveyRounds, setSurveyRounds] = useState([]); 
    const [loadingData, setLoadingData] = useState(true); 
    const [fetchError, setFetchError] = useState(null);   

    const navigate = useNavigate();
    const token = localStorage.getItem("token");

    const fetchData = useCallback(async () => {
        if (!token || isTokenExpired(token)) { // Pass token to isTokenExpired
            localStorage.removeItem('token');
            navigate('/login');
            return;
        }

        setLoadingData(true);
        setFetchError(null);
        let isMounted = true; 

        try {
            const [cohortResponse, questionResponse, surveyResponse] = await Promise.all([
                fetch(`${config.backendUrl}/cohorts`, { headers: { "Authorization": `Bearer ${token}`, "Accept": "application/json" } }),
                fetch(`${config.backendUrl}/questions`, { headers: { "Authorization": `Bearer ${token}`, "Accept": "application/json" } }),
                fetch(`${config.backendUrl}/survey_rounds`, { headers: { "Authorization": `Bearer ${token}`, "Accept": "application/json" } })
            ]);

            if (!isMounted) return;

            if (!cohortResponse.ok) throw new Error(`Failed to fetch cohorts: ${cohortResponse.status}`);
            const cohortsDataRaw = await cohortResponse.json();
            // Assuming backend returns [{cohort_id, cohort_name}, ...] directly now based on previous fixes
            const formattedCohorts = cohortsDataRaw.map(c => ({ cohort_id: c.cohort_id, cohort_name: c.cohort_name }));


            if (!questionResponse.ok) throw new Error(`Failed to fetch questions: ${questionResponse.status}`);
            const questionsData = await questionResponse.json();

            if (!surveyResponse.ok) throw new Error(`Failed to fetch survey rounds: ${surveyResponse.status}`);
            const surveyRoundsData = await surveyResponse.json();

            if (isMounted) {
                setCohorts(formattedCohorts);
                setQuestions(questionsData);
                setSurveyRounds(surveyRoundsData);
            }

        } catch (error) {
            console.error("Error fetching initial data:", error);
            if (isMounted) setFetchError(error.message || "Failed to load necessary data.");
        } finally {
            if (isMounted) setLoadingData(false); 
        }
        return () => { isMounted = false; };
    }, [token, navigate]); 

    useEffect(() => {
        const cleanup = fetchData();
        // React 18+ doesn't strictly need to return a function if fetchData itself returns one for cleanup.
        // However, if fetchData doesn't return a cleanup, and you need one for this effect, define it here.
        // For this case, fetchData's returned cleanup is sufficient.
    }, [fetchData]);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormError(null); 
        setFormSuccess(null);
        switch (name) {
            case "surveyName": setSurveyName(value); break;
            case "description": setDescription(value); break;
            case "startDate": setStartDate(value); break;
            case "endDate": setEndDate(value); break;
            // cohort selection is handled by handleCohortChange
            default: break;
        }
    };

    const handleCohortChange = (event) => {
        const {
          target: { value },
        } = event;
        setSelectedCohortIds(
          // On autofill we get a stringified value.
          typeof value === 'string' ? value.split(',') : value,
        );
        setFormError(null);
        setFormSuccess(null);
      };

    const handleQuestionSelection = (questionId) => {
        setFormSuccess(null); 
        if (selectedQuestionIds.includes(questionId)) {
            setSelectedQuestionIds(selectedQuestionIds.filter((id) => id !== questionId));
        } else {
            setSelectedQuestionIds([...selectedQuestionIds, questionId]);
        }
    };

    const handleSelectAll = () => {
        setFormSuccess(null);
        if (selectedQuestionIds.length === questions.length && questions.length > 0) {
            setSelectedQuestionIds([]); 
        } else {
            setSelectedQuestionIds(questions.map((question) => question.question_id));
        }
    };

     const clearCreateForm = (showSuccess = false) => {
        setSurveyName("");
        setDescription("");
        setStartDate("");
        setEndDate("");
        setSelectedCohortIds([]); // Reset selected cohorts
        setSelectedQuestionIds([]);
        setFormError(null);
         if (!showSuccess) {
             setFormSuccess(null); 
         }
    }

    const handleSubmit = async (event) => {
        event.preventDefault(); 
        setFormError(null);
        setFormSuccess(null);
        setIsSubmitting(true); 

        if (!surveyName || !startDate || !endDate || selectedCohortIds.length === 0 || selectedQuestionIds.length === 0) {
            setFormError("Please fill in Survey Name, Start Date, End Date, select at least one Cohort, and at least one Question.");
            setIsSubmitting(false);
            return;
        }
        if (new Date(endDate) < new Date(startDate)) {
            setFormError("End Date cannot be before Start Date.");
            setIsSubmitting(false);
            return;
        }

        let allSuccessful = true;
        const errors = [];
        const createdSurveyNames = [];

        for (const cohortId of selectedCohortIds) {
            const surveyData = {
                survey_name: surveyName,
                description,
                start_date: startDate,
                end_date: endDate,
                cohort_id: cohortId, 
                question_ids: selectedQuestionIds,
            };

            try {
                const response = await fetch(`${config.backendUrl}/survey_rounds`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(surveyData),
                });

                 let responseData;
                 try { responseData = await response.json(); } catch (e) { responseData = null; } 

                if (!response.ok) {
                    allSuccessful = false;
                    const cohortName = cohorts.find(c => c.cohort_id === cohortId)?.cohort_name || `ID ${cohortId}`;
                    errors.push(`Failed for cohort ${cohortName}: ${responseData?.message || response.statusText}`);
                    // Continue to next cohort even if one fails
                } else {
                    createdSurveyNames.push(surveyName); // Or a more specific name if backend returns it
                }
            } catch (error) {
                allSuccessful = false;
                const cohortName = cohorts.find(c => c.cohort_id === cohortId)?.cohort_name || `ID ${cohortId}`;
                errors.push(`Network or unexpected error for cohort ${cohortName}: ${error.message}`);
                console.error(`Error creating survey for cohort ${cohortId}:`, error);
            }
        } // End of loop

        setIsSubmitting(false); 

        if (allSuccessful) {
            setFormSuccess(`Survey "${surveyName}" created successfully for all selected cohorts! Refreshing list...`);
            clearCreateForm(true); 
            fetchData(); 
        } else {
            setFormError(`Some surveys could not be created. Errors: ${errors.join("; ")}`);
            // Optionally, still refresh data if some were successful
            if (createdSurveyNames.length > 0) {
                fetchData();
            }
        }
    };

    const handleDuplicateSurvey = async (surveyId) => {
         clearCreateForm(); 
         setFormError(null);
         setFormSuccess(null);
         const surveyToDuplicate = surveyRounds.find(s => s.survey_id === surveyId);

         if (surveyToDuplicate) {
            setSurveyName(`${surveyToDuplicate.survey_name} (Copy)`);
            setDescription(surveyToDuplicate.description || "");
            // Dates and cohorts are intentionally left blank for user to fill

            setLoadingData(true); 
            try {
                  const qResponse = await fetch(`${config.backendUrl}/survey_rounds/${surveyId}/questions`, {
                      headers: { "Authorization": `Bearer ${token}` }
                  });
                  if (!qResponse.ok) throw new Error("Failed to fetch original survey questions for duplication");

                  const originalQuestionsRaw = await qResponse.json();
                  // Assuming response is array of question objects {question_id, ...}
                  const originalQuestionIds = originalQuestionsRaw.map(q => q.question_id); 
                  setSelectedQuestionIds(originalQuestionIds); 
                  setFormSuccess("Survey details loaded into form. Please set new dates and select cohort(s).");
                  window.scrollTo({ top: 0, behavior: 'smooth' }); 
             } catch(err) {
                 console.error("Error fetching questions for duplication:", err);
                 setFormError("Could not load questions from the original survey.");
                 clearCreateForm();
                 setSurveyName(`${surveyToDuplicate.survey_name} (Copy)`); 
             } finally {
                 setLoadingData(false); 
             }
         } else {
             setFormError("Could not find survey details to duplicate.");
         }
    };


    return (
        <Box sx={{ p: 2, maxWidth: '1100px', margin: 'auto' }}>
            <Typography variant="h4" gutterBottom component="h1">
                360° Survey Round Management
            </Typography>

            {fetchError && <Alert severity="error" sx={{ mb: 2 }}>{fetchError}</Alert>}

             <Card variant="outlined" sx={{ mb: 3 }}>
                 <CardContent>
                     <Typography variant="h6" gutterBottom>Create New 360° Survey Round</Typography>
                     {formError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError(null)}>{formError}</Alert>}
                     {formSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFormSuccess(null)}>{formSuccess}</Alert>}

                     <form onSubmit={handleSubmit}>
                         <Grid container spacing={2}>
                             <Grid item xs={12} sm={6}>
                                 <TextField label="Survey Name" name="surveyName" value={surveyName} onChange={handleInputChange} fullWidth required margin="dense" size="small"/>
                             </Grid>
                             <Grid item xs={12} sm={6}>
                                  <FormControl fullWidth margin="dense" required size="small">
                                     <InputLabel id="cohort-multi-select-label">Assign to Cohort(s)</InputLabel>
                                     <Select
                                         labelId="cohort-multi-select-label"
                                         id="cohort-multi-select"
                                         multiple
                                         value={selectedCohortIds}
                                         onChange={handleCohortChange}
                                         input={<OutlinedInput label="Assign to Cohort(s)" />}
                                         renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                              {selected.map((value) => {
                                                const cohortObj = cohorts.find(c => c.cohort_id === value);
                                                return <Chip key={value} label={cohortObj ? cohortObj.cohort_name : value} size="small"/>;
                                              })}
                                            </Box>
                                          )}
                                     >
                                         {cohorts && cohorts.map((c) => ( 
                                            <MenuItem key={c.cohort_id} value={c.cohort_id}> 
                                                {c.cohort_name} 
                                            </MenuItem> 
                                         ))}
                                     </Select>
                                 </FormControl>
                             </Grid>
                              <Grid item xs={12} sm={6}>
                                  <TextField label="Start Date" name="startDate" type="date" value={startDate} onChange={handleInputChange} fullWidth required margin="dense" size="small" InputLabelProps={{ shrink: true }} />
                              </Grid>
                               <Grid item xs={12} sm={6}>
                                  <TextField label="End Date" name="endDate" type="date" value={endDate} onChange={handleInputChange} fullWidth required margin="dense" size="small" InputLabelProps={{ shrink: true }} />
                              </Grid>
                              <Grid item xs={12}>
                                 <TextField label="Description (Optional)" name="description" value={description} onChange={handleInputChange} fullWidth margin="dense" multiline rows={2} size="small"/>
                             </Grid>

                             <Grid item xs={12}>
                                  <Typography variant="subtitle1" gutterBottom sx={{mt: 1}}>Select Questions *</Typography>
                                  <Button onClick={handleSelectAll} variant="outlined" size="small" sx={{ mb: 1 }}>
                                      {selectedQuestionIds.length === questions.length && questions.length > 0 ? "Deselect All" : "Select All"}
                                  </Button>
                                  <Paper variant="outlined" sx={{ maxHeight: '200px', overflow: 'auto', mb: 2 }}>
                                      <List dense>
                                          {loadingData ? <CircularProgress sx={{m:2}}/> : questions && questions.length > 0 ? (
                                              questions.map((question) => (
                                                  <ListItem key={question.question_id} disablePadding>
                                                      <ListItemButton onClick={() => handleQuestionSelection(question.question_id)} dense>
                                                          <ListItemIcon sx={{minWidth: 'auto', mr: 1}}>
                                                              <Checkbox
                                                                  edge="start"
                                                                  checked={selectedQuestionIds.includes(question.question_id)}
                                                                  tabIndex={-1}
                                                                  disableRipple
                                                                  size="small"
                                                              />
                                                          </ListItemIcon>
                                                          <Typography variant="body2">{question.question_text}</Typography>
                                                      </ListItemButton>
                                                  </ListItem>
                                              ))
                                            ) : ( <ListItem><Typography variant="body2" color="text.secondary">No questions available.</Typography></ListItem> )
                                          }
                                      </List>
                                  </Paper>
                             </Grid>

                             <Grid item xs={12}>
                                  <Button type="submit" variant="contained" startIcon={<AddCircleOutline />} disabled={isSubmitting}>
                                      {isSubmitting ? <CircularProgress size={24} /> : "Create Survey Round(s)"}
                                  </Button>
                                   <Button variant="outlined" onClick={() => clearCreateForm(false)} sx={{ml: 2}} disabled={isSubmitting}>
                                       Clear Form
                                   </Button>
                             </Grid>
                         </Grid>
                     </form>
                 </CardContent>
             </Card>


            <Divider sx={{ my: 3 }} />

             <Typography variant="h6" gutterBottom>Existing Survey Rounds</Typography>
              {loadingData ? ( <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}><CircularProgress /></Box> ) : (
                <TableContainer component={Paper}>
                     <Table stickyHeader size="small" aria-label="existing survey rounds table">
                         <TableHead>
                             <TableRow>
                                 <TableCell>Name</TableCell>
                                 <TableCell>Cohort</TableCell>
                                 <TableCell>Start</TableCell>
                                 <TableCell>End</TableCell>
                                 <TableCell align="center">Questions</TableCell>
                                 <TableCell align="center">Responses</TableCell>
                                 <TableCell align="center">Actions</TableCell>
                             </TableRow>
                         </TableHead>
                         <TableBody>
                             {surveyRounds && surveyRounds.length > 0 ? (
                                 surveyRounds.map((survey) => (
                                     <TableRow key={survey.survey_id} hover>
                                         <TableCell sx={{ verticalAlign: 'top', maxWidth: '250px' }}>
                                             <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: 'break-word' }}>{survey.survey_name}</Typography>
                                             {survey.description && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, wordBreak: 'break-word' }}>{survey.description}</Typography>}
                                         </TableCell>
                                         <TableCell sx={{ verticalAlign: 'top' }}>{survey.cohort_name || <Typography variant="caption" color="text.secondary">N/A</Typography>}</TableCell>
                                         <TableCell sx={{ verticalAlign: 'top' }}>{survey.start_date ? new Date(survey.start_date).toLocaleDateString() : 'N/A'}</TableCell>
                                         <TableCell sx={{ verticalAlign: 'top' }}>{survey.end_date ? new Date(survey.end_date).toLocaleDateString() : 'N/A'}</TableCell>
                                         <TableCell align="center" sx={{ verticalAlign: 'top' }}>{survey.question_count ?? 'N/A'}</TableCell>
                                         <TableCell align="center" sx={{ verticalAlign: 'top' }}>{survey.response_count ?? 'N/A'}</TableCell>
                                         <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                                             <Tooltip title="Duplicate Survey (Loads into form above)">
                                                  <Button size="small" onClick={() => handleDuplicateSurvey(survey.survey_id)} startIcon={<DuplicateIcon />} variant="outlined">
                                                     Duplicate
                                                 </Button>
                                             </Tooltip>
                                         </TableCell>
                                     </TableRow>
                                 ))
                             ) : (
                                 <TableRow>
                                     <TableCell colSpan={7} align="center">No existing survey rounds found.</TableCell>
                                 </TableRow>
                             )}
                         </TableBody>
                     </Table>
                 </TableContainer>
            )}
        </Box>
    );
}

export default SurveyManagement;
